import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { apiService } from '../../services/apiService';
import { CallInsightsAnalysis } from '../adherence/types';
import type { SalesPlaybookUsage } from './PlaybookUsagePanel';
import SalesIntelligenceV2Section from './SalesIntelligenceV2Section';
import KnowledgeBaseSection from './KnowledgeBaseSection';
import PlaybookSection from './PlaybookSection';
import CallSummaryTab from './summary/CallSummaryTab';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface CallInsightsTabProps {
  sessionId: string;
  callInsights?: CallInsightsAnalysis;
  salesPlaybookUsage?: SalesPlaybookUsage;
  speechRateAlerts: SpeechRateAlert[];
  isAdmin: boolean;
  onAnalysisUpdated: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

const TOTAL_DIMENSIONS = 6;
const MAX_POLL_ATTEMPTS = 100;

const DIMENSION_LABELS: Record<string, string> = {
  objection_faq: 'Objections / FAQs',
  landmine: 'Landmines',
  competitor: 'Competitors',
  playbook: 'Playbook',
  sales_intelligence: 'Sales Intelligence',
  tone_analysis: 'Tone Analysis',
};

function formatGeneratedAt(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

// Tailwind tone for a 0-100 score: green >=80, amber >=60, red below.
function scoreTone(score: number): string {
  if (score >= 80) return 'bg-green-50 text-green-700 border-green-200';
  if (score >= 60) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

/**
 * Headline Call Quality Score badge. The score is the same per-call composite
 * the super-admin rollup aggregates (handling items + playbook, pooled), stored
 * on the call by post_call_analysis. Hidden when the call had nothing to score.
 */
const CallQualityBadge: React.FC<{
  score?: number | null;
  breakdown?: CallInsightsAnalysis['call_quality_breakdown'];
}> = ({ score, breakdown }) => {
  if (score === null || score === undefined) return null;
  const parts: string[] = [`${breakdown?.handling_item_count ?? 0} handling item(s)`];
  if (breakdown?.playbook_used) {
    parts.push(`playbook ${breakdown.playbook_score ?? '—'}`);
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${scoreTone(
        score
      )}`}
      title={`Call Quality Score — pooled handling + playbook (${parts.join(', ')})`}
    >
      <span className="uppercase tracking-wider text-[10px] font-bold opacity-70">Call quality</span>
      {score}%
    </span>
  );
};

interface SectionAccordionProps {
  id: string;
  label: string;
  headerColor: string;
  borderColor: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const SectionAccordion: React.FC<SectionAccordionProps> = ({
  label,
  headerColor,
  borderColor,
  isExpanded,
  onToggle,
  children,
}) => (
  <div className={`border-l-4 ${borderColor} bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200`}>
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-5 py-4 ${headerColor} transition-colors text-left`}
    >
      <span className="text-sm font-bold uppercase tracking-wider">{label}</span>
      <span
        className="text-xs transition-transform duration-200 flex-shrink-0"
        style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}
      >
        &#9660;
      </span>
    </button>
    {isExpanded && (
      <div className="border-t border-gray-100">
        {children}
      </div>
    )}
  </div>
);

const CallInsightsTab: React.FC<CallInsightsTabProps> = ({
  sessionId,
  callInsights,
  salesPlaybookUsage,
  speechRateAlerts,
  isAdmin,
  onAnalysisUpdated,
  onNavigateToTimestamp,
}) => {
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'summary' | 'details'>('summary');
  const [expandedSection, setExpandedSection] = useState<string | null>('sales_intelligence');
  const [detailsInitialSubSection, setDetailsInitialSubSection] = useState<string | undefined>(undefined);
  const pollCountRef = useRef(0);

  const handleSwitchToDetails = useCallback(
    (section: 'sales_intelligence' | 'knowledge_base' | 'playbook', subSection?: string) => {
      setExpandedSection(section);
      setDetailsInitialSubSection(subSection);
      setActiveSubTab('details');
    },
    []
  );

  const toggleSection = useCallback((id: string) => {
    setExpandedSection((prev) => (prev === id ? null : id));
  }, []);

  // Auto-poll while pending
  const isPending = callInsights?.status === 'pending';
  useEffect(() => {
    if (!isPending) {
      pollCountRef.current = 0;
      return;
    }
    const intervalId = setInterval(() => {
      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(intervalId);
        return;
      }
      pollCountRef.current += 1;
      onAnalysisUpdated();
    }, 6000);
    return () => clearInterval(intervalId);
  }, [isPending, onAnalysisUpdated]);

  const handleReanalyze = async () => {
    setIsReanalyzing(true);
    setReanalyzeError(null);
    try {
      const response = await apiService.post(
        `/admin/sessions/${sessionId}/call-insights-reanalyze`
      );
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to start re-analysis');
      }
      onAnalysisUpdated();
    } catch (err: any) {
      setReanalyzeError(err?.message ?? 'An unexpected error occurred.');
    } finally {
      setIsReanalyzing(false);
    }
  };

  const completedDimensions = callInsights?.dimensions_complete ?? [];

  // --- Empty state ---
  if (!callInsights) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-semibold text-gray-600 mb-2">No Analysis Yet</p>
        <p className="text-sm text-gray-400 max-w-sm mb-5">
          Run a Call Insights analysis to get AI-powered insights on knowledge base handling,
          playbook adherence, and sales intelligence.
        </p>
        {isAdmin && (
          <button
            onClick={handleReanalyze}
            disabled={isReanalyzing}
            className="bg-blue-600 text-white px-5 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            type="button"
          >
            {isReanalyzing ? 'Starting Analysis...' : 'Run Analysis'}
          </button>
        )}
        {reanalyzeError && (
          <p className="mt-3 text-sm text-red-600">{reanalyzeError}</p>
        )}
      </div>
    );
  }

  // --- Failed state ---
  if (callInsights.status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-semibold text-red-600 mb-2">Analysis Failed</p>
        {callInsights.error && (
          <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded px-4 py-2 max-w-md mb-4">
            {callInsights.error}
          </p>
        )}
        {isAdmin && (
          <button
            onClick={handleReanalyze}
            disabled={isReanalyzing}
            className="bg-blue-600 text-white px-5 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            type="button"
          >
            {isReanalyzing ? 'Starting...' : 'Retry Analysis'}
          </button>
        )}
        {reanalyzeError && (
          <p className="mt-3 text-sm text-red-600">{reanalyzeError}</p>
        )}
      </div>
    );
  }

  // --- Pending / Complete state ---
  const completedCount = completedDimensions.length;
  const progressPct = Math.round((completedCount / TOTAL_DIMENSIONS) * 100);

  return (
    <div className="space-y-3">
      {/* Top meta bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {callInsights.status === 'complete' && (
            <CallQualityBadge
              score={callInsights.call_quality_score}
              breakdown={callInsights.call_quality_breakdown}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {reanalyzeError && (
            <span className="text-xs text-red-600">{reanalyzeError}</span>
          )}
          {isAdmin && callInsights.status === 'complete' && (
            <button
              onClick={handleReanalyze}
              disabled={isReanalyzing}
              className="text-xs bg-white text-gray-600 border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              type="button"
            >
              {isReanalyzing ? 'Re-analyzing...' : 'Re-analyze'}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar when pending */}
      {isPending && (
        <div className="flex flex-col items-center py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-semibold text-gray-700">Analysis in progress...</p>
          </div>
          {completedCount > 0 ? (
            <div className="w-full max-w-xs">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>
                  {completedCount} of {TOTAL_DIMENSIONS} dimensions complete
                </span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <ul className="mt-3 space-y-1 text-left">
                {completedDimensions.map((dim) => (
                  <li key={dim} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="text-green-500 font-bold">&#10003;</span>
                    {DIMENSION_LABELS[dim] ?? dim}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-400 max-w-sm text-center">
              The AI is analyzing the session across 5 dimensions. This may take a minute or two.
            </p>
          )}
        </div>
      )}

      {/* Sub-tab toggle + content — only shown once analysis is complete */}
      {callInsights.status === 'complete' && (
        <div className="space-y-4">
          {/* Sub-tab navigation */}
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveSubTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSubTab === 'summary'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Summary
            </button>
            <button
              type="button"
              onClick={() => setActiveSubTab('details')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSubTab === 'details'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
          </div>

          {/* Summary sub-tab */}
          {activeSubTab === 'summary' && (
            <CallSummaryTab
              sessionId={sessionId}
              callInsights={callInsights}
              salesPlaybookUsage={salesPlaybookUsage}
              speechRateAlerts={speechRateAlerts}
              isAdmin={isAdmin}
              onAnalysisUpdated={onAnalysisUpdated}
              onNavigateToTimestamp={onNavigateToTimestamp}
              onSwitchToDetails={handleSwitchToDetails}
            />
          )}

          {/* Details sub-tab */}
          {activeSubTab === 'details' && (
            <div className="space-y-4">
              <SectionAccordion
                id="sales_intelligence"
                label="Sales Intelligence"
                headerColor="bg-blue-50 hover:bg-blue-100 text-blue-900"
                borderColor="border-l-blue-500"
                isExpanded={expandedSection === 'sales_intelligence'}
                onToggle={() => toggleSection('sales_intelligence')}
              >
                <div className="p-5">
                  <SalesIntelligenceV2Section
                    sessionId={sessionId}
                    intelligence={callInsights?.sales_intelligence!}
                    callInsights={callInsights}
                    speechRateAlerts={speechRateAlerts}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                    onNavigateToTimestamp={onNavigateToTimestamp}
                  />
                </div>
              </SectionAccordion>

              <SectionAccordion
                id="knowledge_base"
                label="Knowledge Base Handling"
                headerColor="bg-amber-50 hover:bg-amber-100 text-amber-900"
                borderColor="border-l-amber-500"
                isExpanded={expandedSection === 'knowledge_base'}
                onToggle={() => toggleSection('knowledge_base')}
              >
                <div className="p-5">
                  <KnowledgeBaseSection
                    sessionId={sessionId}
                    callInsights={callInsights}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                    onNavigateToTimestamp={onNavigateToTimestamp}
                    initialExpandedAccordion={detailsInitialSubSection}
                  />
                </div>
              </SectionAccordion>

              <SectionAccordion
                id="playbook"
                label="Playbook"
                headerColor="bg-green-50 hover:bg-green-100 text-green-900"
                borderColor="border-l-green-500"
                isExpanded={expandedSection === 'playbook'}
                onToggle={() => toggleSection('playbook')}
              >
                <div className="p-5">
                  <PlaybookSection
                    sessionId={sessionId}
                    callInsights={callInsights}
                    salesPlaybookUsage={salesPlaybookUsage}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                  />
                </div>
              </SectionAccordion>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CallInsightsTab;
